// Phase RV-4b — DriftDecisionModal.
//
// Renders when a pending drift event exists for the user. Three options
// (N09 mandatory): accept / replace / skip. Compose I0 primitives only.
//
// Promotion-not-restriction copy. The market moved — this is the
// standard working as intended, not an error.

import { useState } from "react";
import { Badge, Eyebrow, HairlineRow, Pill } from "../super-acca";
import { marketLabel, selectionLabel } from "../../exchange/market-format.ts";
import {
  usePendingDrift,
  type DriftDecision,
  type DriftEvent,
  type DriftReplacementCandidate,
  type UsePendingDriftState,
} from "../../rollover/usePendingDrift.ts";

export interface DriftDecisionModalProps {
  /** Override for tests so we can render without the hook. */
  state?: UsePendingDriftState;
}

const BTN_STYLE: React.CSSProperties = {
  background: "transparent",
  border: "1px solid var(--sa-hairline)",
  borderRadius: 3,
  padding: "8px 12px",
  color: "var(--sa-fg-primary)",
  fontFamily: "var(--sa-font-mono)",
  fontSize: 11,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  cursor: "pointer",
};

function DriftLegBlock({
  event,
}: {
  event: DriftEvent;
}): JSX.Element {
  const driftBadge =
    event.drift_kind === "below_range" ? "BELOW BAND" : "ABOVE BAND";
  return (
    <div style={{ padding: "8px 0" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <Pill>{event.leg_market}</Pill>
        <Pill>{event.leg_pick}</Pill>
        <Badge variant="ghost">{driftBadge}</Badge>
      </div>
      <div
        style={{
          fontFamily: "var(--sa-font-mono)",
          fontSize: 11,
          color: "var(--sa-fg-secondary)",
          marginTop: 8,
          lineHeight: 1.55,
        }}
      >
        Original odds: {event.original_odds.toFixed(2)}× → Current:{" "}
        {event.current_odds.toFixed(2)}×
      </div>
      <div
        style={{
          fontFamily: "var(--sa-font-mono)",
          fontSize: 10,
          color: "var(--sa-fg-muted)",
          marginTop: 4,
        }}
      >
        Tier band {event.tier_min.toFixed(2)}× – {event.tier_max.toFixed(2)}×
      </div>
    </div>
  );
}

function ReplacementPreview({
  replacement,
}: {
  replacement: DriftReplacementCandidate | null;
}): JSX.Element {
  if (!replacement) {
    return (
      <div
        style={{
          fontFamily: "var(--sa-font-mono)",
          fontSize: 11,
          color: "var(--sa-fg-muted)",
          marginTop: 6,
        }}
        data-test="drift-no-replacement"
      >
        No qualifying replacement candidate available for this slot.
      </div>
    );
  }
  return (
    <div
      style={{
        fontFamily: "var(--sa-font-mono)",
        fontSize: 11,
        color: "var(--sa-fg-secondary)",
        marginTop: 6,
        lineHeight: 1.55,
      }}
      data-test="drift-replacement-preview"
    >
      Replace with: {replacement.match_name}
      {replacement.league ? ` · ${replacement.league}` : ""} · {marketLabel(replacement.market)} ·{" "}
      {selectionLabel(replacement.market, replacement.pick)}
      {replacement.odds !== null ? ` · ${replacement.odds.toFixed(2)}×` : ""}
      {replacement.confidence_score !== null
        ? ` · ${replacement.confidence_score.toFixed(0)}/100 confidence`
        : ""}
    </div>
  );
}

export function DriftDecisionModal({
  state,
}: DriftDecisionModalProps = {}): JSX.Element | null {
  const hook = usePendingDrift();
  const s = state ?? hook;
  const [pendingDecision, setPendingDecision] = useState<DriftDecision | null>(
    null,
  );

  const event = s.pendingEvent;
  if (!event) return null;

  const handleClick = async (decision: DriftDecision): Promise<void> => {
    if (s.decideLoading) return;
    setPendingDecision(decision);
    try {
      if (decision === "replace") {
        await s.decide(event.event_id, "replace", s.replacement?.fixture_id);
      } else {
        await s.decide(event.event_id, decision);
      }
    } finally {
      setPendingDecision(null);
    }
  };

  const canReplace = s.replacement !== null;

  return (
    <section
      data-test="drift-decision-modal"
      style={{
        padding: 14,
        border: "1px solid var(--sa-hairline)",
        borderRadius: 4,
        marginTop: 14,
        marginBottom: 14,
        background: "var(--sa-bg-secondary, transparent)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <Eyebrow>Odds Drift Detected</Eyebrow>
        <Badge variant="amber">QUALITY GATE</Badge>
      </div>
      <div
        style={{
          fontFamily: "var(--sa-font-mono)",
          fontSize: 12,
          color: "var(--sa-fg-secondary)",
          marginTop: 10,
          lineHeight: 1.55,
        }}
      >
        Market moved. Choose your move — the agents found this drift outside
        the standard tier band.
      </div>

      <div style={{ marginTop: 12 }}>
        <HairlineRow first padY={4}>
          <DriftLegBlock event={event} />
        </HairlineRow>
      </div>

      <div style={{ marginTop: 14 }}>
        <Eyebrow>Replacement Preview</Eyebrow>
        <ReplacementPreview replacement={s.replacement} />
      </div>

      {s.error ? (
        <div
          data-test="drift-error"
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 10,
            color: "var(--sa-fg-muted)",
            marginTop: 10,
          }}
        >
          {s.error}
        </div>
      ) : null}

      <div
        style={{
          marginTop: 14,
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          data-test="drift-accept"
          disabled={s.decideLoading}
          onClick={() => {
            void handleClick("accept");
          }}
          style={BTN_STYLE}
        >
          {pendingDecision === "accept" ? "Accepting…" : "Accept the drift"}
        </button>
        <button
          type="button"
          data-test="drift-replace"
          disabled={s.decideLoading || !canReplace}
          onClick={() => {
            void handleClick("replace");
          }}
          style={{
            ...BTN_STYLE,
            opacity: canReplace ? 1 : 0.5,
            cursor: canReplace ? "pointer" : "not-allowed",
          }}
        >
          {pendingDecision === "replace" ? "Replacing…" : "Replace this leg"}
        </button>
        <button
          type="button"
          data-test="drift-skip"
          disabled={s.decideLoading}
          onClick={() => {
            void handleClick("skip");
          }}
          style={BTN_STYLE}
        >
          {pendingDecision === "skip" ? "Skipping…" : "Skip the cycle"}
        </button>
      </div>

      <div
        style={{
          fontFamily: "var(--sa-font-mono)",
          fontSize: 10,
          color: "var(--sa-fg-muted)",
          marginTop: 10,
          lineHeight: 1.5,
        }}
      >
        Accept — proceed with current odds; slip stays published. Replace —
        swap in the next-best evaluated leg. Skip — chain pauses, no advance.
        Per N12: agents found this drift outside the standard.
      </div>
    </section>
  );
}
